import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Grid,
  Chip,
  LinearProgress,
  InputAdornment,
  Tooltip,
  CircularProgress,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Avatar,
  TablePagination,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Search as SearchIcon,
  Devices as DevicesIcon,
  School as SchoolIcon,
  Quiz as QuizIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LockReset as LockResetIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  VpnKey as VpnKeyIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { baseUrl, config, requestID } from "../../common/utils";
import { IonPage } from '@ionic/react';

const StudentManagementDashboard = () => {
  // State management
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [initialLoad, setInitialLoad] = useState(true);
  
  // Filter states
  const [courseFilter, setCourseFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  
  // Courses state
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  
  // Dialog states
  const [deviceDialog, setDeviceDialog] = useState({
    open: false,
    student: null,
    loading: false
  });
  
  const [examDialog, setExamDialog] = useState({
    open: false,
    student: null,
    exams: [],
    selectedExams: [],
    resetAll: false,
    loading: false
  });
  
  const [csvDialog, setCsvDialog] = useState({
    open: false,
    loading: false,
    file: null,
    selectedCourse: '',
    selectedLevel: '',
    courseName: '',
    levelName: ''
  });
  
  const [historyDialog, setHistoryDialog] = useState({
    open: false,
    student: null,
    history: [],
    loading: false
  });
  
  const [addStudentDialog, setAddStudentDialog] = useState({
    open: false,
    loading: false,
    formData: {
      email: '',
      firstName: '',
      lastName: '',
      middleName: '',
      phone: '',
      level: '',
      cou_id: '',
      status: '1',
      username: '',
      password: 'password123'
    },
    errors: {}
  });
  
  // Password Reset Dialog State
  const [passwordResetDialog, setPasswordResetDialog] = useState({
    open: false,
    loading: false,
    password: '',
    confirmPassword: '',
  });

  // Reset All Devices Dialog State
  const [resetAllDevicesDialog, setResetAllDevicesDialog] = useState({
    open: false,
    loading: false,
    confirmationText: ''
  });
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalStudents, setTotalStudents] = useState(0);
  
  // Search debouncing
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  // File input ref
  const fileInputRef = useRef(null);

  // LEVELS constant
  const LEVELS = ['year1', 'year2', 'year3', 'year4', 'year5'];

  // Fetch students on component mount and when filters/pagination change
  useEffect(() => {
    fetchCourses();
    fetchStudents();
  }, [page, rowsPerPage, courseFilter, levelFilter]);

  // Debounced search effect
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for search
    if (searchTerm.trim() !== '') {
      const timeoutId = setTimeout(() => {
        handleSearch(searchTerm);
      }, 500); // 500ms debounce delay

      setSearchTimeout(timeoutId);
    } else if (searchTerm === '') {
      // If search is cleared, fetch regular page
      fetchStudents();
    }

    // Cleanup
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTerm]);

  // Filter students based on search and filters (for client-side filtering when needed)
  useEffect(() => {
    let filtered = [...students];
    
    if (courseFilter) {
      filtered = filtered?.filter(student => student.cou_id === courseFilter);
    }
    
    if (levelFilter) {
      filtered = filtered?.filter(student => student.level === levelFilter);
    }
    
    setFilteredStudents(filtered);
  }, [students, courseFilter, levelFilter]);

  // Fetch students from API with search functionality
  const fetchStudents = async (search = '', isSearchCall = false) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      
      // For search, always get page 1 with search term
      if (isSearchCall) {
        formData.append("page", 1);
        formData.append("limit", rowsPerPage);
        formData.append("search", search);
      } else {
        // For regular pagination
        formData.append("page", page + 1);
        formData.append("limit", rowsPerPage);
        formData.append("search", searchTerm); // Include search term if it exists
      }
      
      if (courseFilter) {
        formData.append("cou_id", courseFilter);
      }
      
      if (levelFilter) {
        formData.append("level", levelFilter);
      }

      const response = await axios.post(
        `${baseUrl()}Cbt_StudentManagements/getStudents`,
        formData,
        config
      );

      console.log("Fetched students:", response.data);
      if (response.data.status) {
        setStudents(response.data.data);
        setTotalStudents(response.data.pagination?.total || 0);
        
        // If this is a search call, reset to page 0
        if (isSearchCall) {
          setPage(0);
        }
      } else {
        showSnackbar(response.data.message, 'error');
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      showSnackbar('Failed to fetch students', 'error');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  // Handle search across all records
  const handleSearch = useCallback((searchValue) => {
    if (searchValue.trim() === '') {
      // If search is empty, fetch regular data
      fetchStudents('', false);
    } else {
      // Fetch search results from API
      fetchStudents(searchValue, true);
    }
  }, [courseFilter, levelFilter, rowsPerPage]);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    fetchStudents('', false);
    setPage(0);
  };

  // Fetch all courses for dropdown
  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(`${baseUrl()}Cbt_Courses/index`, formData, config);
      
      if (response.data.status === true) {
        setCourses(response.data.data || []);
      } else {
        showSnackbar('Failed to load courses', 'error');
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      showSnackbar('Failed to load courses', 'error');
    } finally {
      setLoadingCourses(false);
    }
  };

  // Reset student device - Clear device_hash in initkey table
  const handleResetDevice = async () => {
    if (!deviceDialog.student) return;

    setDeviceDialog(prev => ({ ...prev, loading: true }));

    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("user_id", deviceDialog.student.user_id);

      const response = await axios.post(
        `${baseUrl()}Cbt_StudentManagements/resetStudentDevice`,
        formData,
        config
      );

      if (response.data.status) {
        showSnackbar('Device reset successfully. Student can now log in from any device.', 'success');
        fetchStudents();
        setDeviceDialog({ open: false, student: null, loading: false });
      } else {
        showSnackbar(response.data.message, 'error');
      }
    } catch (error) {
      console.error('Error resetting device:', error);
      showSnackbar('Failed to reset device', 'error');
    } finally {
      setDeviceDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Reset all devices at once
  const handleResetAllDevices = async () => {
    if (resetAllDevicesDialog.confirmationText.toLowerCase() !== 'confirm') {
      showSnackbar('Please type "confirm" to proceed', 'error');
      return;
    }

    setResetAllDevicesDialog(prev => ({ ...prev, loading: true }));

    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(
        `${baseUrl()}Cbt_StudentManagements/resetAllDevices`,
        formData,
        config
      );

      if (response.data.status) {
        showSnackbar('All student devices have been reset successfully! Students can now log in from any device.', 'success');
        fetchStudents(); // Refresh the component
        setResetAllDevicesDialog({
          open: false,
          loading: false,
          confirmationText: ''
        });
      } else {
        showSnackbar(response.data.message || 'Failed to reset devices', 'error');
      }
    } catch (error) {
      console.error('Error resetting devices:', error);
      showSnackbar('Failed to reset devices. Please try again.', 'error');
    } finally {
      setResetAllDevicesDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Open exam reset dialog - FIXED: Now properly handles the response
  const handleOpenExamReset = async (student) => {
    try {
      // Fetch available exams for this student
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("user_id", student.user_id);

      const response = await axios.post(
        `${baseUrl()}Cbt_StudentManagements/getStudentExamHistory`,
        formData,
        config
      );

      console.log("Exam history response:", response.data);
      
      if (response.data.status) {
        setExamDialog({
          open: true,
          student,
          exams: response.data.data || [],
          selectedExams: [],
          resetAll: false,
          loading: false
        });
      } else {
        showSnackbar('No exam history found for this student', 'info');
      }
    } catch (error) {
      console.error('Error fetching exam history:', error);
      showSnackbar('Failed to load exam history', 'error');
    }
  };

  // Reset exam availability - FIXED: Now properly sends the data
  const handleResetExams = async () => {
    if (!examDialog.student) return;

    setExamDialog(prev => ({ ...prev, loading: true }));

    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("user_id", examDialog.student.user_id);
      
      if (examDialog.resetAll) {
        examDialog.selectedExams.forEach((examId, index) => {
          formData.append(`exam_ids[${index}]`, examId);
        });
      } else {
        // Send selected exam IDs as array
        examDialog.selectedExams.forEach((examId, index) => {
          formData.append(`exam_ids[${index}]`, examId);
        });
      }

      console.log("Sending reset data:", {
        user_id: examDialog.student.user_id,
        reset_all: examDialog.resetAll,
        selectedExams: examDialog.selectedExams
      });

      const response = await axios.post(
        `${baseUrl()}Cbt_StudentManagements/resetExamAvailability`,
        formData,
        config
      );

      console.log("Reset exam response:", response.data);

      if (response.data.status) {
        showSnackbar('Exam availability reset successfully. Student can retake selected exams.', 'success');
        fetchStudents();
        setExamDialog({
          open: false,
          student: null,
          exams: [],
          selectedExams: [],
          resetAll: false,
          loading: false
        });
      } else {
        showSnackbar(response.data.message || 'Failed to reset exams', 'error');
      }
    } catch (error) {
      console.error('Error resetting exams:', error);
      showSnackbar('Failed to reset exams', 'error');
    } finally {
      setExamDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle CSV upload with selected course and level
  const handleCsvUpload = async () => {
    if (!csvDialog.file) {
      showSnackbar('Please select a CSV file', 'warning');
      return;
    }

    if (!csvDialog.selectedCourse) {
      showSnackbar('Please select a course for the bulk upload', 'warning');
      return;
    }

    if (!csvDialog.selectedLevel) {
      showSnackbar('Please select a level for the bulk upload', 'warning');
      return;
    }

    setCsvDialog(prev => ({ ...prev, loading: true }));

    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("csv_file", csvDialog.file);
      formData.append("cou_id", csvDialog.selectedCourse);
      formData.append("level", csvDialog.selectedLevel);

      const response = await axios.post(
        `${baseUrl()}Cbt_StudentManagements/bulkUploadStudents`,
        formData,
        config
      );

      console.log(response);
      if (response.data.status) {
        showSnackbar(`Bulk upload completed: ${response.data.results.success} created, ${response.data.results.updated} updated, ${response.data.results.failed} failed`, 'success');
        fetchStudents();
        setCsvDialog({ open: false, loading: false, file: null, selectedCourse: '', selectedLevel: '', courseName: '', levelName: '' });
        
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        showSnackbar(response.data.message, 'error');
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      showSnackbar('Failed to upload CSV', 'error');
    } finally {
      setCsvDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Download CSV template with placeholders for course and level
  const downloadTemplate = async () => {
    try {
      const response = await axios.get(
        `${baseUrl()}Cbt_StudentManagements/downloadTemplate`,
        {
          ...config,
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `students_template_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      showSnackbar('Failed to download template', 'error');
    }
  };

  // View student history
  const handleViewHistory = async (student) => {
    setHistoryDialog(prev => ({ ...prev, open: true, student, loading: true }));

    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("user_id", student.user_id);

      const response = await axios.post(
        `${baseUrl()}Cbt_StudentManagements/getStudentExamHistory`,
        formData,
        config
      );

      if (response.data.status) {
        setHistoryDialog(prev => ({
          ...prev,
          history: response.data.data || [],
          loading: false
        }));
      } else {
        showSnackbar(response.data.message, 'error');
        setHistoryDialog(prev => ({ ...prev, open: false, loading: false }));
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      showSnackbar('Failed to load history', 'error');
      setHistoryDialog(prev => ({ ...prev, open: false, loading: false }));
    }
  };

  // Handle add student form changes
  const handleAddStudentChange = (field, value) => {
    setAddStudentDialog(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        [field]: value
      },
      errors: {
        ...prev.errors,
        [field]: ''
      }
    }));
  };

  // Validate add student form
  const validateAddStudentForm = () => {
    const errors = {};
    const { email, firstName, lastName } = addStudentDialog.formData;

    if (!email.trim()) errors.email = 'Email is required';
    if (!firstName.trim()) errors.firstName = 'First name is required';
    if (!lastName.trim()) errors.lastName = 'Last name is required';

    return errors;
  };

  // Add single student
  const handleAddStudent = async () => {
    const errors = validateAddStudentForm();
    
    if (Object.keys(errors).length > 0) {
      setAddStudentDialog(prev => ({ ...prev, errors }));
      return;
    }

    setAddStudentDialog(prev => ({ ...prev, loading: true }));

    try {
      // Generate username from email if not provided
      const formData = { ...addStudentDialog.formData };
      if (!formData.username.trim()) {
        formData.username = formData.email.split('@')[0];
      }

      const postData = new FormData();
      postData.append("requestID", requestID());
      postData.append("email", formData.email);
      postData.append("firstName", formData.firstName);
      postData.append("lastName", formData.lastName);
      postData.append("middleName", formData.middleName);
      postData.append("phone", formData.phone);
      postData.append("username", formData.username);
      postData.append("password", formData.password);
      postData.append("role_id", 'student');
      postData.append("level", formData.level);
      postData.append("cou_id", formData.cou_id);

      const response = await axios.post(
        `${baseUrl()}Cbt_StudentManagements/createStudent`,
        postData,
        config
      );

      console.log(response);

      if (response.data.status === 'true') {
        showSnackbar('Student added successfully', 'success');
        fetchStudents();
        setAddStudentDialog({
          open: false,
          loading: false,
          formData: {
            email: '',
            firstName: '',
            lastName: '',
            middleName: '',
            phone: '',
            level: '',
            cou_id: '',
            status: '1',
            username: '',
            password: 'password123'
          },
          errors: {}
        });
      } else {
        showSnackbar(response?.data.message || 'Failed to add student', 'error');
      }
    } catch (error) {
      console.error('Error adding student:', error);
      showSnackbar('Failed to add student', 'error');
    } finally {
      setAddStudentDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle CSV course selection
  const handleCsvCourseChange = (cou_id) => {
    const selectedCourse = courses.find(course => course.cou_id === cou_id);
    setCsvDialog(prev => ({
      ...prev,
      selectedCourse: cou_id,
      courseName: selectedCourse?.cou_name || cou_id
    }));
  };

  // Handle CSV level selection
  const handleCsvLevelChange = (level) => {
    setCsvDialog(prev => ({
      ...prev,
      selectedLevel: level,
      levelName: level
    }));
  };

  // Open password reset dialog
  const handleOpenPasswordReset = () => {
    setPasswordResetDialog({
      open: true,
      loading: false,
      password: '',
      confirmPassword: '',
    });
  };

  // Handle password reset dialog changes
  const handlePasswordResetChange = (field, value) => {
    setPasswordResetDialog(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Reset all student passwords
  const handleResetAllPasswords = async () => {
    const { password, confirmPassword } = passwordResetDialog;
    
    if (!password.trim()) {
      showSnackbar('Please enter a password', 'error');
      return;
    }
    
    if (password.length < 6) {
      showSnackbar('Password must be at least 6 characters', 'error');
      return;
    }
    
    if (password !== confirmPassword) {
      showSnackbar('Passwords do not match', 'error');
      return;
    }

    setPasswordResetDialog(prev => ({ ...prev, loading: true }));

    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("new_password", password);
      formData.append("user_type", 'student');

      const response = await axios.post(
        `${baseUrl()}Cbt_StudentManagements/resetAllPasswords`,
        formData,
        config
      );

      if (response.data.status) {
        showSnackbar('All student passwords have been reset successfully!', 'success');
        setPasswordResetDialog({
          open: false,
          loading: false,
          password: '',
          confirmPassword: '',
        });
      } else {
        showSnackbar(response.data.message || 'Failed to reset passwords', 'error');
      }
    } catch (error) {
      console.error('Error resetting passwords:', error);
      showSnackbar('Failed to reset passwords. Please try again.', 'error');
    } finally {
      setPasswordResetDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Helper functions
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getDeviceStatus = (student) => {
    if (student.device_hash && student.device_hash.trim() !== '') {
      return student.loggedin_device_id_status === '1' ? 'Device Locked' : 'Device Registered';
    }
    return 'No Device';
  };

  const getDeviceColor = (student) => {
    if (student.device_hash && student.device_hash.trim() !== '') {
      return student.loggedin_device_id_status === '1' ? 'warning' : 'success';
    }
    return 'default';
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || '?';
  };

  const getFullName = (firstName, lastName, middleName) => {
    const names = [firstName, middleName, lastName].filter(Boolean);
    return names.join(' ') || 'Unknown';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvDialog(prev => ({ ...prev, file }));
    } else {
      showSnackbar('Please select a valid CSV file', 'error');
    }
  };

  const handleSelectAllExams = (checked) => {
    if (checked && examDialog.exams.length > 0) {
      const allExamIds = examDialog.exams?.map(exam => exam.ex_id);
      setExamDialog(prev => ({ 
        ...prev, 
        selectedExams: allExamIds,
        resetAll: true
      }));
    } else {
      setExamDialog(prev => ({ 
        ...prev, 
        selectedExams: [],
        resetAll: false
      }));
    }
  };

  const handleSelectExam = (examId) => {
    setExamDialog(prev => {
      const isSelected = prev.selectedExams.includes(examId);
      const newSelected = isSelected
        ? prev.selectedExams.filter(id => id !== examId)
        : [...prev.selectedExams, examId];
      
      return {
        ...prev,
        selectedExams: newSelected,
        resetAll: newSelected.length === prev.exams.length
      };
    });
  };

  // Open CSV dialog
  const handleOpenCsvDialog = () => {
    setCsvDialog({ 
      open: true, 
      loading: false, 
      file: null, 
      selectedCourse: '', 
      selectedLevel: '',
      courseName: '',
      levelName: ''
    });
  };

  // Extract unique courses and levels for filters
  const uniqueCourses = [...new Set(students?.map(s => s.cou_id).filter(Boolean))];
  const uniqueLevels = [...new Set(students?.map(s => s.level).filter(Boolean))];

  // Calculate devices registered count
  const devicesRegisteredCount = students?.filter(s => s.device_hash && s.device_hash.trim() !== '').length;

  // =================== PAGE LOADING SPINNER ===================
  if (initialLoad) {
    return (
        <IonPage className='pushDown'>
          
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        bgcolor: 'white'
      }}>
        <AppBar 
          position="fixed" 
          sx={{ 
            zIndex: (theme) => theme.zIndex.drawer + 1,
            bgcolor: 'white',
            color: 'text.primary',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Toolbar sx={{ minHeight: 64 }}>
            <Container maxWidth="xl" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SchoolIcon fontSize="large" color="primary" />
                <Box>
                  <Typography variant="h6" fontWeight={700} noWrap>
                    Student Management Dashboard
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    Loading...
                  </Typography>
                </Box>
              </Box>
            </Container>
          </Toolbar>
        </AppBar>
        
        <Box 
          component="main"
          sx={{
            flexGrow: 1,
            mt: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ mb: 3, color: 'primary.main' }} />
          <Typography variant="h5" color="primary" gutterBottom>
            Loading Student Dashboard...
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please wait while we fetch student data
          </Typography>
        </Box>
      </Box>

        </IonPage>
    );
  }

  return (
    <IonPage className='pushDown'>
    
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      bgcolor: 'white',
      overflow: 'hidden'
    }}>
     <AppBar
  position="fixed"
  sx={{
    zIndex: (theme) => theme.zIndex.drawer + 1,
    bgcolor: "white",
    color: "text.primary",
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
    borderBottom: "1px solid",
    borderColor: "divider"
  }}
>
  <Toolbar
    disableGutters
    sx={{
      minHeight: 56,
      px: 2
    }}
  >
    <Container
      maxWidth="xl"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2
      }}
    >
      {/* LEFT: Title */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <SchoolIcon fontSize="small" color="primary" />
        <Typography
          variant="subtitle1"
          fontWeight={600}
          noWrap
        >
          Student Management
        </Typography>
      </Box>

      {/* RIGHT: Actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "nowrap"
        }}
      >
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={downloadTemplate}>
          CSV
        </Button>

        <Button size="small" variant="contained" color="secondary" startIcon={<VpnKeyIcon />} onClick={handleOpenPasswordReset}>
          Reset All Password
        </Button>

        <Button
          size="small"
          variant="contained"
          color="error"
          startIcon={<DevicesIcon />}
          onClick={() =>
            setResetAllDevicesDialog({
              open: true,
              loading: false,
              confirmationText: ""
            })
          }
        >
          Reset All Devices
        </Button>

        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddStudentDialog({ ...addStudentDialog, open: true })}
        >
          Add Student
        </Button>

        <Button
          size="small"
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={handleOpenCsvDialog}
        >
          Upload
        </Button>

        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchStudents}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>
    </Container>
  </Toolbar>
</AppBar>

      {/* Main Content Area - Scrollable */}
      <Box 
        component="main"
        sx={{
          flexGrow: 1,
          mt: 8,
          overflow: 'auto',
          bgcolor: 'white'
        }}
      >
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Search and Filters - Moved to main content */}
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search students by name, email, or phone..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#fff' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={handleClearSearch}
                          sx={{ color: '#fff' }}
                        >
                          <Typography variant="caption">Clear</Typography>
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      color: '#fff',
                      backgroundColor: 'rgba(0, 0, 0, 0.35)',
                      borderRadius: 1,

                      '& input::placeholder': {
                        color: 'rgba(255,255,255,0.7)',
                      },

                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(24, 23, 23, 0.3)',
                      },

                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.6)',
                      },

                      '&.Mui-focused': {
                        backgroundColor: 'rgba(0, 0, 0, 0.45)',
                      },

                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#fff',
                      },
                    },
                  }}
                />
                {searchTerm && (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5, display: 'block' }}>
                    Searching across all {totalStudents} students...
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: 'white' }}>Course</InputLabel>
                  <Select
                    value={courseFilter}
                    label="Course"
                    onChange={(e) => setCourseFilter(e.target.value)}
                    sx={{
                      color: 'white',
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.3)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.5)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'white',
                      },
                      '.MuiSvgIcon-root': {
                        color: 'white',
                      }
                    }}
                    disabled={loadingCourses}
                  >
                    <MenuItem value="">All Courses</MenuItem>
                    {courses?.map(course => (
                      <MenuItem key={course.cou_id} value={course.cou_id}>
                        {course.cou_name || course.cou_id}
                      </MenuItem>
                    ))}
                  </Select>
                  {loadingCourses && (
                    <FormHelperText sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Loading courses...
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: 'white' }}>Level</InputLabel>
                  <Select
                    value={levelFilter}
                    label="Level"
                    onChange={(e) => setLevelFilter(e.target.value)}
                    sx={{
                      color: 'white',
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.3)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.5)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'white',
                      },
                      '.MuiSvgIcon-root': {
                        color: 'white',
                      }
                    }}
                  >
                    <MenuItem value="">All Levels</MenuItem>
                    {LEVELS.map(level => (
                      <MenuItem key={level} value={level}>{level}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 2, textAlign: 'center', boxShadow: 3 }}>
                <Typography variant="h3" color="primary" fontWeight="bold">
                  {totalStudents}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Students
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 2, textAlign: 'center', boxShadow: 3 }}>
                <Typography variant="h3" color="success.main" fontWeight="bold">
                  {students?.filter(s => Number(s.user_status) === 1).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Students
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 2, textAlign: 'center', boxShadow: 3 }}>
                <Typography variant="h3" color="warning.main" fontWeight="bold">
                  {students?.filter(s => s.device_hash && s.device_hash.trim() !== '' && Number(s.loggedin_device_id_status) === 1).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Device Locked
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 2, textAlign: 'center', boxShadow: 3 }}>
                <Typography variant="h3" color="info.main" fontWeight="bold">
                  {devicesRegisteredCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Devices Registered
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Search Results Info */}
          {searchTerm && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Showing search results for: <strong>"{searchTerm}"</strong> 
                {filteredStudents.length > 0 && ` (${filteredStudents.length} found)`}
              </Typography>
            </Alert>
          )}

          {/* Students Table */}
          <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3, boxShadow: 3 }}>
            {loading && <LinearProgress />}
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="medium">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 700 }}>STUDENT</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>CONTACT</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>COURSE & LEVEL</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>DEVICE</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>EXAMS</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">ACTIONS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredStudents?.map((student) => (
                    <TableRow key={student.user_id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {getInitials(student.firstName, student.lastName)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {getFullName(student.firstName, student.lastName, student.middleName)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              @{student.username}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                            <EmailIcon fontSize="small" />
                            {student.email}
                          </Typography>
                          {student.altEmail && (
                            <Typography variant="caption" color="text.secondary">
                              Alt: {student.altEmail}
                            </Typography>
                          )}
                          {student.phone && (
                            <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                              <PhoneIcon fontSize="small" />
                              {student.phone}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                        <Chip
  label={student.cou_name || student.cou_id}
  size="small"
  variant="outlined"
  sx={{
    mb: 0.5,
    bgcolor: 'red',
    color: 'white',
    borderColor: 'orange'
  }}
/>

                          <Typography variant="body2" color="text.secondary">
                            {student.level || 'No Level'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <DevicesIcon fontSize="small" color={getDeviceColor(student)} />
                          <Typography variant="body2">
                            {getDeviceStatus(student)}
                          </Typography>
                          {student.device_hash && student.device_hash.trim() !== '' && (
                            <Tooltip title={student.loggedin_device_id_status === '1' ? "Device locked to specific device" : "Device registered"}>
                              {student.loggedin_device_id_status === '1' ? (
                                <BlockIcon fontSize="small" color="warning" />
                              ) : (
                                <CheckCircleIcon fontSize="small" color="success" />
                              )}
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          <Tooltip title="Total exams taken">
                            <Chip
                              label={`${student.exams_taken || 0}`}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          </Tooltip>
                          <Tooltip title="Completed exams">
                            <Chip
                              label={`✓${student.completed_exams || 0}`}
                              size="small"
                              variant="outlined"
                              color="success"
                            />
                          </Tooltip>
                          <Tooltip title="Pending exams">
                            <Chip
                              label={`⋯${student.pending_exams || 0}`}
                              size="small"
                              variant="outlined"
                              color="warning"
                            />
                          </Tooltip>
                          {student.active_exams_count > 0 && (
                            <Tooltip title="Active exams">
                              <Chip
                                label={`▶${student.active_exams_count}`}
                                size="small"
                                color="error"
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" gap={1} justifyContent="center">
                          <Tooltip title="View Exam History">
                            <IconButton
                              size="small"
                              onClick={() => handleViewHistory(student)}
                              sx={{ color: 'info.main' }}
                            >
                              <HistoryIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset Device">
                            <IconButton
                              size="small"
                              onClick={() => setDeviceDialog({ open: true, student, loading: false })}
                              sx={{ color: 'warning.main' }}
                              disabled={!student.device_hash || student.device_hash.trim() === ''}
                            >
                              <LockResetIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset Exam Availability">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenExamReset(student)}
                              sx={{ color: 'secondary.main' }}
                            >
                              <QuizIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {filteredStudents.length === 0 && !loading && (
              <Box p={3} textAlign="center">
                <PersonIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography color="textSecondary" variant="h6" gutterBottom>
                  {searchTerm || courseFilter || levelFilter
                    ? 'No students found matching your filters.'
                    : 'No students found. Add students using the "Add Student" or "Bulk Upload" buttons.'}
                </Typography>
                {searchTerm && (
                  <Button
                    variant="outlined"
                    onClick={handleClearSearch}
                    sx={{ mt: 2 }}
                  >
                    Clear Search
                  </Button>
                )}
              </Box>
            )}

            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={searchTerm ? filteredStudents.length : totalStudents}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              labelDisplayedRows={({ from, to, count }) => {
                if (searchTerm) {
                  return `Showing ${from}-${to} of ${count} search results`;
                }
                return `Showing ${from}-${to} of ${count}`;
              }}
            />
          </Paper>
        </Container>
      </Box>

      {/* =================== DIALOGS =================== */}

      {/* Simple & Fast Password Reset Dialog */}
      <Dialog
        open={passwordResetDialog.open}
        onClose={() => !passwordResetDialog.loading && setPasswordResetDialog({
          open: false,
          loading: false,
          password: '',
          confirmPassword: '',
        })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'secondary.main', color: 'white' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <VpnKeyIcon />
            <Typography variant="h6">Reset All Student Passwords</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Enter a new password for all students. They will need to use this password to log in.
          </Alert>

          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={passwordResetDialog.password}
              onChange={(e) => handlePasswordResetChange('password', e.target.value)}
              sx={{ mb: 2 }}
              size="small"
              helperText="Minimum 6 characters"
              autoFocus
            />
            
            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              value={passwordResetDialog.confirmPassword}
              onChange={(e) => handlePasswordResetChange('confirmPassword', e.target.value)}
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => !passwordResetDialog.loading && setPasswordResetDialog({
              open: false,
              loading: false,
              password: '',
              confirmPassword: '',
            })}
            variant="outlined"
            disabled={passwordResetDialog.loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetAllPasswords}
            variant="contained"
            color="secondary"
            disabled={passwordResetDialog.loading}
            startIcon={passwordResetDialog.loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {passwordResetDialog.loading ? 'Resetting...' : 'Reset Passwords'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset All Devices Dialog */}
      <Dialog
        open={resetAllDevicesDialog.open}
        onClose={() => !resetAllDevicesDialog.loading && setResetAllDevicesDialog({
          open: false,
          loading: false,
          confirmationText: ''
        })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon />
            <Typography variant="h6">Reset All Student Devices</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              ⚠️ WARNING: This action is irreversible!
            </Typography>
            <Typography variant="body2">
              This will reset device registration for ALL students. They will need to log in again from their devices.
            </Typography>
          </Alert>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Affected Students:</strong> {devicesRegisteredCount} students with registered devices will be affected.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              This will clear the device_hash for all student accounts, allowing them to log in from any device.
            </Typography>
          </Alert>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              To confirm, please type <strong>"confirm"</strong> below:
            </Typography>
            <TextField
              fullWidth
              value={resetAllDevicesDialog.confirmationText}
              onChange={(e) => setResetAllDevicesDialog(prev => ({ 
                ...prev, 
                confirmationText: e.target.value 
              }))}
              placeholder="Type 'confirm' to proceed"
              size="small"
              autoFocus
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => !resetAllDevicesDialog.loading && setResetAllDevicesDialog({
              open: false,
              loading: false,
              confirmationText: ''
            })}
            variant="outlined"
            disabled={resetAllDevicesDialog.loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetAllDevices}
            variant="contained"
            color="error"
            disabled={resetAllDevicesDialog.loading || resetAllDevicesDialog.confirmationText.toLowerCase() !== 'confirm'}
            startIcon={resetAllDevicesDialog.loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {resetAllDevicesDialog.loading ? 'Resetting...' : 'Reset All Devices'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Student Dialog */}
      <Dialog
        open={addStudentDialog.open}
        onClose={() => !addStudentDialog.loading && setAddStudentDialog({ ...addStudentDialog, open: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon />
            <Typography variant="h6">Add New Student</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }} className="pushDown">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name *"
                value={addStudentDialog.formData.firstName}
                onChange={(e) => handleAddStudentChange('firstName', e.target.value)}
                error={!!addStudentDialog.errors.firstName}
                helperText={addStudentDialog.errors.firstName}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name *"
                value={addStudentDialog.formData.lastName}
                onChange={(e) => handleAddStudentChange('lastName', e.target.value)}
                error={!!addStudentDialog.errors.lastName}
                helperText={addStudentDialog.errors.lastName}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Middle Name"
                value={addStudentDialog.formData.middleName}
                onChange={(e) => handleAddStudentChange('middleName', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email *"
                type="email"
                value={addStudentDialog.formData.email}
                onChange={(e) => handleAddStudentChange('email', e.target.value)}
                error={!!addStudentDialog.errors.email}
                helperText={addStudentDialog.errors.email}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                value={addStudentDialog.formData.username}
                onChange={(e) => handleAddStudentChange('username', e.target.value)}
                helperText="Leave blank to generate from email"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={addStudentDialog.formData.phone}
                onChange={(e) => handleAddStudentChange('phone', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Course</InputLabel>
                <Select
                  value={addStudentDialog.formData.cou_id}
                  label="Course"
                  onChange={(e) => handleAddStudentChange('cou_id', e.target.value)}
                  disabled={loadingCourses}
                >
                  <MenuItem value="">Select Course</MenuItem>
                  {courses?.map(course => (
                    <MenuItem key={course.cou_id} value={course.cou_id}>
                      {course.cou_name || course.cou_id}
                    </MenuItem>
                  ))}
                </Select>
                {loadingCourses && (
                  <FormHelperText>Loading courses...</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Level</InputLabel>
                <Select
                  value={addStudentDialog.formData.level}
                  label="Level"
                  onChange={(e) => handleAddStudentChange('level', e.target.value)}
                >
                  <MenuItem value="">Select Level</MenuItem>
                  {LEVELS.map(level => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={addStudentDialog.formData.status}
                  label="Status"
                  onChange={(e) => handleAddStudentChange('status', e.target.value)}
                >
                  <MenuItem value="1">Active</MenuItem>
                  <MenuItem value="0">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={addStudentDialog.formData.password}
                onChange={(e) => handleAddStudentChange('password', e.target.value)}
                helperText="Default: password123"
                size="small"
              />
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 2 }}>
            Student will be created with role "student" and default password.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => !addStudentDialog.loading && setAddStudentDialog({ ...addStudentDialog, open: false })}
            variant="outlined"
            disabled={addStudentDialog.loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddStudent}
            variant="contained"
            color="primary"
            disabled={addStudentDialog.loading}
            startIcon={addStudentDialog.loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {addStudentDialog.loading ? 'Adding...' : 'Add Student'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Device Dialog */}
      <Dialog
        open={deviceDialog.open}
        onClose={() => !deviceDialog.loading && setDeviceDialog({ open: false, student: null, loading: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <LockResetIcon />
            <Typography variant="h6">Reset Student Device</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {deviceDialog.student && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will clear the device_hash in the initkey table, allowing the student to log in from any device.
              </Alert>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {getInitials(deviceDialog.student.firstName, deviceDialog.student.lastName)}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {getFullName(deviceDialog.student.firstName, deviceDialog.student.lastName, deviceDialog.student.middleName)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {deviceDialog.student.email}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    <DevicesIcon fontSize="small" color={getDeviceColor(deviceDialog.student)} />
                    <Chip
                      label={getDeviceStatus(deviceDialog.student)}
                      size="small"
                      color={getDeviceColor(deviceDialog.student)}
                    />
                  </Box>
                </Box>
              </Box>
              {deviceDialog.student.device_hash && deviceDialog.student.device_hash.trim() !== '' && (
                <Typography variant="body2" color="text.secondary">
                  Current Device Hash: {deviceDialog.student.device_hash.substring(0, 20)}...
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => !deviceDialog.loading && setDeviceDialog({ open: false, student: null, loading: false })}
            variant="outlined"
            disabled={deviceDialog.loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetDevice}
            variant="contained"
            color="warning"
            startIcon={deviceDialog.loading ? <CircularProgress size={20} color="inherit" /> : null}
            disabled={deviceDialog.loading}
          >
            {deviceDialog.loading ? 'Resetting...' : 'Reset Device'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Exam Availability Dialog - FIXED: Now properly shows up */}
      <Dialog
        open={examDialog.open}
        onClose={() => !examDialog.loading && setExamDialog({ 
          open: false, 
          student: null, 
          exams: [], 
          selectedExams: [], 
          resetAll: false, 
          loading: false 
        })}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: '80vh' } }}
      >
        <DialogTitle sx={{ bgcolor: 'secondary.main', color: 'white' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <QuizIcon />
            <Typography variant="h6">Reset Exam Availability</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {examDialog.student && (
            <>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {getInitials(examDialog.student.firstName, examDialog.student.lastName)}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {getFullName(examDialog.student.firstName, examDialog.student.lastName, examDialog.student.middleName)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {examDialog.student.email}
                  </Typography>
                </Box>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                This will delete records from exam_attempts_cbt table for selected exams, allowing the student to retake them from the beginning.
              </Alert>

              {examDialog.exams.length === 0 ? (
                <Alert severity="info">
                  No exam history found for this student.
                </Alert>
              ) : (
                <>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Select Exams to Reset ({examDialog.exams.length} total)
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={examDialog.resetAll}
                          onChange={(e) => handleSelectAllExams(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Select All"
                    />
                  </Box>

                  <TableContainer sx={{ maxHeight: 300, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={examDialog.resetAll}
                              onChange={(e) => handleSelectAllExams(e.target.checked)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>Exam Title</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Started</TableCell>
                          <TableCell>Score</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {examDialog.exams?.map((exam) => (
                          <TableRow key={exam.attempt_id} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={examDialog.selectedExams.includes(exam.ex_id)}
                                onChange={() => handleSelectExam(exam.ex_id)}
                                color="primary"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {exam.ex_title || exam.exam_title || 'Untitled Exam'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {exam.ex_id || exam.exam_id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={exam.status}
                                color={exam.status === 'completed' ? 'success' : 'warning'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {formatDate(exam.start_time)}
                            </TableCell>
                            <TableCell>
                              {exam.status === 'completed' ? (
                                <Typography variant="body2" color="success.main" fontWeight={600}>
                                  {exam.correct_answers || exam.correct || 0}/{exam.total_questions || 0}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  In progress
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {examDialog.selectedExams.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {examDialog.resetAll ? (
                        'All exams will be reset for this student. All attempts and answers will be deleted.'
                      ) : (
                        `${examDialog.selectedExams.length} exam(s) selected for reset. Attempts and answers will be deleted.`
                      )}
                    </Alert>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => !examDialog.loading && setExamDialog({ 
              open: false, 
              student: null, 
              exams: [], 
              selectedExams: [], 
              resetAll: false, 
              loading: false 
            })}
            variant="outlined"
            disabled={examDialog.loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetExams}
            variant="contained"
            color="secondary"
            disabled={examDialog.loading || examDialog.selectedExams.length === 0}
            startIcon={examDialog.loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {examDialog.loading ? 'Processing...' : 'Reset Selected Exams'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Upload Dialog with Course and Level Selection */}
      <Dialog
        open={csvDialog.open}
        onClose={() => !csvDialog.loading && setCsvDialog({ 
          open: false, 
          loading: false, 
          file: null, 
          selectedCourse: '', 
          selectedLevel: '', 
          courseName: '', 
          levelName: '' 
        })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'info.main', color: 'white' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <CloudUploadIcon />
            <Typography variant="h6">Bulk Upload Students</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Upload a CSV file with student information. All students will be assigned to the selected course and level.
          </Alert>

          {/* Course and Level Selection */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Course for Upload</InputLabel>
                <Select
                  value={csvDialog.selectedCourse}
                  label="Course for Upload"
                  onChange={(e) => handleCsvCourseChange(e.target.value)}
                  disabled={loadingCourses || csvDialog.loading}
                >
                  <MenuItem value="">Select Course</MenuItem>
                  {courses?.map(course => (
                    <MenuItem key={course.cou_id} value={course.cou_id}>
                      {course.cou_name || course.cou_id}
                    </MenuItem>
                  ))}
                </Select>
                {loadingCourses && (
                  <FormHelperText>Loading courses...</FormHelperText>
                )}
                {csvDialog.selectedCourse && (
                  <FormHelperText sx={{ color: 'success.main' }}>
                    Selected: {csvDialog.courseName}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Level for Upload</InputLabel>
                <Select
                  value={csvDialog.selectedLevel}
                  label="Level for Upload"
                  onChange={(e) => handleCsvLevelChange(e.target.value)}
                  disabled={csvDialog.loading}
                >
                  <MenuItem value="">Select Level</MenuItem>
                  {LEVELS.map(level => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
                {csvDialog.selectedLevel && (
                  <FormHelperText sx={{ color: 'success.main' }}>
                    Selected: {csvDialog.levelName}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>

          {/* File Upload Section */}
          <Box sx={{ mb: 3 }}>
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="csv-upload"
              disabled={!csvDialog.selectedCourse || !csvDialog.selectedLevel}
            />
            <label htmlFor="csv-upload">
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: csvDialog.file ? 'success.main' : 'divider',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: (!csvDialog.selectedCourse || !csvDialog.selectedLevel) ? 'not-allowed' : 'pointer',
                  bgcolor: csvDialog.file ? 'success.50' : 'grey.50',
                  opacity: (!csvDialog.selectedCourse || !csvDialog.selectedLevel) ? 0.5 : 1,
                  '&:hover': {
                    borderColor: (!csvDialog.selectedCourse || !csvDialog.selectedLevel) ? 'divider' : 'primary.main',
                    bgcolor: (!csvDialog.selectedCourse || !csvDialog.selectedLevel) ? 'grey.50' : 'action.hover'
                  }
                }}
              >
                {csvDialog.file ? (
                  <Box>
                    <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      File Selected
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {csvDialog.file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(csvDialog.file.size / 1024).toFixed(2)} KB
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      {(!csvDialog.selectedCourse || !csvDialog.selectedLevel) ? 'Select course and level first' : 'Click to upload CSV file'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(!csvDialog.selectedCourse || !csvDialog.selectedLevel) 
                        ? 'Please select a course and level before uploading' 
                        : 'Supports .csv files with proper format'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </label>
          </Box>

          {/* CSV Format Example */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              CSV Format Example (Course and Level will be assigned from selections above):
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, fontFamily: 'monospace', fontSize: 12, bgcolor: 'grey.50' }}>
              Email,First Name,Last Name,Middle Name,Phone,Status (1=active, 0=inactive)<br />
              student1@example.com,John,Doe,Michael,+1234567890,1<br />
              student2@example.com,Jane,Smith,,+1234567891,0<br />
              <br />
              <Typography variant="caption" color="success.main">
                * Note: All students will be assigned to: Course="{csvDialog.courseName || '[Selected Course]'}", Level="{csvDialog.levelName || '[Selected Level]'}"
              </Typography>
            </Paper>
          </Box>

          {/* Upload Summary */}
          {csvDialog.file && csvDialog.selectedCourse && csvDialog.selectedLevel && (
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Upload Summary:</strong><br />
                • File: {csvDialog.file.name}<br />
                • Course: {csvDialog.courseName}<br />
                • Level: {csvDialog.levelName}<br />
                • All students in the CSV will be assigned to this course and level.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={downloadTemplate}
            startIcon={<DownloadIcon />}
            disabled={csvDialog.loading}
            variant="outlined"
          >
            Download Template
          </Button>
          <Button
            onClick={() => !csvDialog.loading && setCsvDialog({ 
              open: false, 
              loading: false, 
              file: null, 
              selectedCourse: '', 
              selectedLevel: '', 
              courseName: '', 
              levelName: '' 
            })}
            variant="outlined"
            disabled={csvDialog.loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCsvUpload}
            variant="contained"
            color="info"
            disabled={csvDialog.loading || !csvDialog.file || !csvDialog.selectedCourse || !csvDialog.selectedLevel}
            startIcon={csvDialog.loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {csvDialog.loading ? 'Uploading...' : 'Upload CSV'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exam History Dialog */}
      <Dialog
        open={historyDialog.open}
        onClose={() => setHistoryDialog({ open: false, student: null, history: [], loading: false })}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: '80vh' } }}
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <HistoryIcon />
            <Typography variant="h6">Exam History</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {historyDialog.student && (
            <>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {getInitials(historyDialog.student.firstName, historyDialog.student.lastName)}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {getFullName(historyDialog.student.firstName, historyDialog.student.lastName, historyDialog.student.middleName)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {historyDialog.student.email}
                  </Typography>
                </Box>
              </Box>

              {historyDialog.loading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : historyDialog.history.length === 0 ? (
                <Alert severity="info">
                  No exam history found for this student.
                </Alert>
              ) : (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Exam</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Start Time</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>End Time</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Score</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {historyDialog.history?.map((exam) => (
                        <TableRow key={exam.attempt_id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {exam.ex_title || exam.exam_title || 'Untitled Exam'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {exam.ex_id || exam.exam_id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={exam.status}
                              color={exam.status === 'completed' ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {formatDate(exam.start_time)}
                          </TableCell>
                          <TableCell>
                            {exam.end_time ? formatDate(exam.end_time) : 'In Progress'}
                          </TableCell>
                          <TableCell align="center">
                            {exam.status === 'completed' && (exam.total_questions > 0 || exam.total_questions) ? (
                              <Box>
                                <Typography variant="body2" fontWeight={600} color="success.main">
                                  {exam.correct_answers || exam.correct || 0}/{exam.total_questions || 0}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {Math.round(((exam.correct_answers || exam.correct || 0) / (exam.total_questions || 1)) * 100)}%
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {exam.start_time && exam.end_time ? (
                              <Typography variant="body2">
                                {Math.round((new Date(exam.end_time) - new Date(exam.start_time)) / 60000)} min
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setHistoryDialog({ open: false, student: null, history: [], loading: false })}
            variant="contained"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
      </IonPage>
  );
};

export default StudentManagementDashboard;